import { z } from 'zod';
import { id, constrainedStrings } from './common';

/**
 * Hex color validation regex
 * Matches: #RGB, #RRGGBB, #RRGGBBAA
 */
const hexColorRegex = /^#([0-9A-Fa-f]{3}){1,2}([0-9A-Fa-f]{2})?$/;

/**
 * Color schema - validates hex color strings
 */
export const colorSchema = z
  .string()
  .regex(hexColorRegex, {
    message: 'Color must be a valid hex color (e.g., #3b82f6, #fff, #ff0000aa)',
  })
  .optional();

/**
 * Model Item Schema
 * Represents a diagram element's data model
 */
export const modelItemSchema = z.object({
  id,
  name: constrainedStrings.name,
  description: constrainedStrings.description.optional(),
  icon: id.optional(),
  color: colorSchema, // NEW: Optional color property
});

/**
 * Array of model items
 */
export const modelItemsSchema = z.array(modelItemSchema);

// ===== TYPE EXPORTS =====

/**
 * Model Item type with color support
 */
export type ModelItemWithColor = z.infer<typeof modelItemSchema>;

// ===== HELPER FUNCTIONS =====

/**
 * Validate if a string is a valid hex color
 * @param color - The color string to validate
 * @returns true if valid hex color
 */
export const isValidHexColor = (color: string): boolean => {
  return hexColorRegex.test(color);
};

/**
 * Normalize hex color to uppercase 6-digit format
 * @param color - The color to normalize (e.g., '#fff')
 * @returns Normalized color (e.g., '#FFFFFF')
 */
export const normalizeHexColor = (color: string): string => {
  // Remove # if present
  let hex = color.replace('#', '');

  // Expand 3-digit to 6-digit
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Add # prefix and uppercase
  return `#${hex.toUpperCase()}`;
};

/**
 * Get default color for new items
 * @returns Default color hex string
 */
export const getDefaultItemColor = (): string => {
  return '#3b82f6'; // Blue
};

/**
 * Validate and sanitize color input
 * @param color - The color to validate
 * @returns Valid color or default color
 */
export const validateAndSanitizeColor = (color: string | undefined): string => {
  if (!color) {
    return getDefaultItemColor();
  }

  try {
    if (isValidHexColor(color)) {
      return normalizeHexColor(color);
    }
  } catch (error) {
    console.warn(`Invalid color: ${color}, using default`);
  }

  return getDefaultItemColor();
};